import { Loader2, X } from 'lucide-react';
import { useEffect, useRef } from 'react';

export default function CancelAttemptDialog({ attempt, loading, error, onCancel, onClose }) {
  const dialogRef = useRef(null);
  const initialFocusRef = useRef(null);
  const loadingRef = useRef(loading);
  const onCloseRef = useRef(onClose);
  loadingRef.current = loading;
  onCloseRef.current = onClose;

  useEffect(() => {
    if (!attempt) return undefined;
    const previouslyFocused = document.activeElement;
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    initialFocusRef.current?.focus();
    const onKeyDown = (event) => {
      if (event.key === 'Escape' && !loadingRef.current) {
        event.preventDefault();
        onCloseRef.current();
        return;
      }
      if (event.key !== 'Tab') return;
      const focusable = [...dialogRef.current.querySelectorAll('button:not(:disabled), [href], input:not(:disabled), select:not(:disabled), textarea:not(:disabled), [tabindex]:not([tabindex="-1"])')];
      if (!focusable.length) return;
      const first = focusable[0];
      const last = focusable[focusable.length - 1];
      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    };
    document.addEventListener('keydown', onKeyDown);
    return () => {
      document.removeEventListener('keydown', onKeyDown);
      document.body.style.overflow = previousOverflow;
      previouslyFocused?.focus?.();
    };
  }, [attempt]);

  if (!attempt) return null;
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-950/40 px-4" role="presentation" onMouseDown={loading ? undefined : onClose}>
      <div
        ref={dialogRef}
        className="w-full max-w-md rounded-lg border border-slate-200 bg-white p-6 shadow-xl"
        role="dialog"
        aria-modal="true"
        aria-labelledby="cancel-attempt-title"
        aria-describedby="cancel-attempt-description"
        onMouseDown={(event) => event.stopPropagation()}
      >
        <div className="flex items-start justify-between gap-4">
          <div>
            <h2 id="cancel-attempt-title" className="text-lg font-semibold text-slate-900">Cancel this payment attempt?</h2>
            <p id="cancel-attempt-description" className="mt-2 text-sm leading-6 text-slate-600">
              This only closes the current attempt. The payment request will remain available so you can continue later.
            </p>
          </div>
          <button type="button" onClick={onClose} disabled={loading} className="rounded p-1 text-slate-400 hover:bg-slate-100 hover:text-slate-700 disabled:opacity-50" aria-label="Close dialog">
            <X className="h-5 w-5" aria-hidden="true" />
          </button>
        </div>
        {error ? <p className="mt-4 rounded border border-rose-200 bg-rose-50 px-3 py-2 text-sm text-rose-700" role="status" aria-live="polite">{error}</p> : null}
        <div className="mt-6 flex justify-end gap-3">
          <button ref={initialFocusRef} type="button" onClick={onClose} disabled={loading} className="rounded border border-slate-300 px-4 py-2 text-sm font-medium text-slate-700 hover:bg-slate-50 disabled:opacity-50">Keep attempt</button>
          <button type="button" onClick={onCancel} disabled={loading} className="inline-flex min-w-28 items-center justify-center gap-2 rounded bg-rose-600 px-4 py-2 text-sm font-semibold text-white hover:bg-rose-700 disabled:cursor-wait disabled:opacity-60">
            {loading ? <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" /> : null}
            Cancel attempt
          </button>
        </div>
      </div>
    </div>
  );
}
